// Trip purpose split — Employment / Education / Other.
//
// A trip-generation sub-step: the person trips a development generates are split
// by purpose, so Education can be distributed to schools (gravity model) and
// mode-split differently, while Employment + Other keep the census journey-to-work
// distribution and the census/manual mode share. All values here are editable
// defaults seeded from the DfT National Travel Survey (England, 2024).
//
// Sources: NTS0502a (trip start time by purpose) for the hourly split;
// NTS0409a/b (trips & distance by purpose and main mode) for the education mode
// share and mean trip length. Employment = commuting + business; Education =
// education + escort-education (the school run); Other = everything else.

// Purpose split (%) by trip START HOUR (0–23): [employment, education, other].
// Seeding a period from the hour of its start time keeps the band flexible —
// e.g. an 08:00 AM peak is education-heavy (school run), a 17:00 PM peak is not.
export const NTS_PURPOSE_BY_HOUR = {
  0: [40.3, 0.0, 59.7], 1: [61.0, 0.0, 39.0], 2: [64.7, 0.8, 34.5], 3: [63.9, 4.5, 31.6],
  4: [67.8, 0.8, 31.4], 5: [68.8, 0.2, 31.0], 6: [65.7, 2.3, 32.0], 7: [44.0, 22.6, 33.4],
  8: [18.9, 53.4, 27.7], 9: [14.3, 8.7, 77.0], 10: [8.1, 1.9, 90.0], 11: [7.7, 3.0, 89.3],
  12: [8.3, 4.1, 87.6], 13: [11.3, 2.1, 86.6], 14: [10.8, 13.9, 75.3], 15: [8.6, 48.5, 42.9],
  16: [23.1, 11.0, 65.9], 17: [27.8, 7.0, 65.2], 18: [17.2, 1.9, 80.9], 19: [10.5, 1.3, 88.2],
  20: [13.5, 1.1, 85.4], 21: [12.9, 1.9, 85.2], 22: [22.2, 0.8, 77.0], 23: [25.6, 0.7, 73.7],
};

// Broad-peak split (%) by hour: the sample-weighted average of the three hours
// centred on each hour (h−1, h, h+1) — e.g. an 08:00 period covers 07:00–09:59,
// a 17:00 period covers 16:00–18:59. Smooths the sharp single-hour spikes (the
// 08:00 school-run peak in particular) into a peak-window view.
export const NTS_PURPOSE_BROAD_BY_HOUR = {
  0: [29.2, 0.6, 70.2], 1: [52.7, 0.2, 47.1], 2: [63.0, 1.8, 35.1], 3: [66.9, 1.3, 31.8],
  4: [68.3, 0.5, 31.1], 5: [66.5, 1.7, 31.7], 6: [51.1, 16.0, 32.9], 7: [30.8, 39.4, 29.8],
  8: [23.7, 34.8, 41.5], 9: [14.8, 28.7, 56.4], 10: [9.8, 4.4, 85.8], 11: [8.0, 3.0, 89.0],
  12: [9.0, 3.1, 87.9], 13: [10.1, 6.9, 83.0], 14: [9.9, 27.6, 62.5], 15: [13.5, 28.4, 58.0],
  16: [18.2, 25.9, 55.9], 17: [23.3, 7.2, 69.5], 18: [20.5, 4.1, 75.5], 19: [14.2, 1.6, 84.2],
  20: [11.8, 1.4, 86.8], 21: [15.2, 1.3, 83.5], 22: [18.2, 1.3, 80.5], 23: [23.9, 0.7, 75.4],
};

// Available seeding bands for the purpose split.
export const PURPOSE_BANDS = [
  { key: 'hour', label: 'Hour-specific (single peak hour)' },
  { key: 'broad', label: 'Broad peak (±1 hour window)' },
];

// All-day average split (%) — the fallback when a period has no usable start time.
export const NTS_PURPOSE_ALL_DAY = { employment: 18.6, education: 5.2, other: 76.2 };

// Education mode share (%) — NTS0409, 2024. Employment and Other keep the app's
// existing census/manual mode share; only Education carries its own default.
export const NTS_EDUCATION_MODE_SHARE = { car: 46.6, rail: 1.7, bus: 6.7, cycle: 1.2, walk: 40.5, other: 3.3 };
export const EDUCATION_CAR_PCT = NTS_EDUCATION_MODE_SHARE.car; // fraction driven -> vehicle trips

// Education gravity-model defaults (see backend /api/education-flows).
// Primary/secondary demand split from the national GIAS pupil counts; decay betas
// calibrated to NTS mean education trip lengths (primary ~2.6 km, secondary ~5.5 km).
export const EDUCATION_GRAVITY_DEFAULTS = {
  primaryShare: 56,      // % of education demand that is primary-age
  betaPrimary: 0.77,
  betaSecondary: 0.36,
  radiusKm: 25,
};

// Parse "HH:MM" (or "HH") to an integer hour 0–23, or null if unusable.
export function hourFromStartTime(startTime) {
  if (!startTime) return null;
  const h = parseInt(String(startTime).slice(0, 2), 10);
  return Number.isInteger(h) && h >= 0 && h <= 23 ? h : null;
}

// NTS default split for a period, seeded from its start-time hour. `band` picks
// the single-hour table ('hour', default) or the broad ±1h peak-window table
// ('broad'). Falls back to the all-day average when the start time is unusable.
// Returns { employment, education, other } as whole %s.
export function defaultPurposeSplit(startTime, band = 'hour') {
  const hr = hourFromStartTime(startTime);
  const table = band === 'broad' ? NTS_PURPOSE_BROAD_BY_HOUR : NTS_PURPOSE_BY_HOUR;
  const row = hr != null ? table[hr] : null;
  if (!row) return { ...NTS_PURPOSE_ALL_DAY };
  return { employment: row[0], education: row[1], other: row[2] };
}

// Normalise a split so its three parts sum to exactly 100 (largest-remainder on
// one decimal), tolerating blank/NaN entries. Used before any calculation relies
// on the split.
export function normaliseSplit(split) {
  const keys = ['employment', 'education', 'other'];
  const vals = keys.map(k => Math.max(0, parseFloat(split?.[k]) || 0));
  const sum = vals.reduce((a, b) => a + b, 0);
  if (sum <= 0) return { employment: 0, education: 0, other: 0 };
  const scaled = vals.map(v => (v / sum) * 100);
  // Round to 1 dp, fix the residual on the largest part so it totals 100.0.
  const rounded = scaled.map(v => Math.round(v * 10) / 10);
  const resid = Math.round((100 - rounded.reduce((a, b) => a + b, 0)) * 10) / 10;
  const maxIdx = scaled.indexOf(Math.max(...scaled));
  rounded[maxIdx] = Math.round((rounded[maxIdx] + resid) * 10) / 10;
  return { employment: rounded[0], education: rounded[1], other: rounded[2] };
}

// The education fraction (0–1) of a period's person trips, from its split.
export function educationFraction(split) {
  const n = normaliseSplit(split);
  return (n.education || 0) / 100;
}
